import { ActiviteRecensement } from "./../models/activite.model";
import { Activite, ActiviteDTO, ElementKey } from "@/models";
import { toast } from "@/store";
import { TYPE } from "@/store/modules/toast.module";
import { Http } from "@/utils/http";

/** Backend calls for the "activité" domain — one HTTP call per method, no local state. */
export default class ActiviteService {
  /** Fetches a single activity by id. */
  public static async activite(id: string): Promise<Activite> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=activite&id=${id}`;
    const { data: response } = await Http.instance.get(targetUrl);
    return response.data;
  }

  /**
   * Creates a new activity.
   * @param optionPreSaisieVehicule Pre-fill the vehicle team slot on creation.
   * @param optionPreSaisieCompetences Pre-fill the competence requirements on creation.
   * @returns The id of the created activity (via `response.data?.id`).
   */
  public static async create(
    activite: ActiviteDTO,
    optionPreSaisieVehicule = false,
    optionPreSaisieCompetences = false
  ): Promise<ElementKey> {
    const response = await Http.postTask(
      "activiteCreate",
      { activite, optionPreSaisieVehicule, optionPreSaisieCompetences },
      "Activité créé"
    );
    return response.data?.id;
  }

  /** Attaches a piece of equipment ("matériel") to an activity. */
  public static async liaisonMateriel(
    activite: string,
    materiel: ElementKey
  ): Promise<ElementKey> {
    const response = await Http.postTask("activiteMateriel", {
      activite,
      materiel,
    });
    return response.data?.id;
  }

  /** Attaches a vehicle to an activity. */
  public static async liaisonVehicule(
    activite: string,
    vehicule: ElementKey
  ): Promise<ElementKey> {
    const response = await Http.postTask("activiteVehicule", {
      activite,
      vehicule,
    });
    return response.data?.id;
  }

  /** Sets the number of volunteers required for a given competence on an activity. */
  public static async liaisonCompetence(
    activite: string,
    competence: ElementKey,
    number: number
  ): Promise<ElementKey> {
    const response = await Http.postTask("activiteBesoin", {
      competence: {
        activite: activite,
        id: competence.id,
        nombre: number,
      },
    });
    return response.data?.id;
  }

  /** Updates an existing activity's fields. */
  public static async update(activite: Activite): Promise<ElementKey> {
    const response = await Http.postTask("activiteUpdate", { activite });
    return response.data?.id;
  }

  /**
   * Deletes an activity.
   * @param notify Whether to notify engaged volunteers of the cancellation.
   */
  public static async delete(id: string, notify: boolean): Promise<boolean> {
    const response = await Http.postTask("activiteDelete", { id, notify });
    return !!response.success;
  }

  /** Cancels an activity (soft — activity stays in place, marked cancelled). */
  public static async annulerActivite(id: string): Promise<boolean> {
    const response = await Http.postTask("activiteAnnuler", { id });
    return !!response.success;
  }

  /** Closes the "recensement" (volunteer sign-up) window for an activity. */
  public static async bloquerRecensement(id: string): Promise<boolean> {
    const response = await Http.postTask("activiteRecensementBloquer", {
      id,
    });
    return !!response.success;
  }

  /** Re-opens the "recensement" window for an activity. */
  public static async ouvrir(id: string): Promise<boolean> {
    const response = await Http.postTask("activiteRecensementOuvrir", { id });
    return !!response.success;
  }

  /** Closes an activity and records the final team ("équipe") who took part. */
  public static async fermerEtEquipe(
    id: string,
    equipe: ActiviteRecensement[]
  ): Promise<boolean> {
    const response = await Http.postTask("activiteFermer", { id, equipe });
    return !!response.success;
  }

  /** Sends a reminder ("relance") message to volunteers not yet responded. */
  public static async relance(id: string, message: string): Promise<boolean> {
    const response = await Http.postTask("activiteRelance", { id, message });
    return !!response.success;
  }

  /** Sends a "waiting list" notification message for an activity. */
  public static async messageAttente(id: string): Promise<boolean> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=messageSrEngage&activite_id=${id}`;
    const { data: response } = await Http.instance.get(targetUrl);
    if (response.error) {
      toast.pushError(response.message ?? "Une erreur s'est produite.");
      return false;
    }
    if (response.success) {
      toast.createAndPushToast(
        response.message ?? "OK",
        "",
        true,
        TYPE.SUCCESS
      );
      return true;
    }
    return false;
  }
}
